import React, { useEffect, useState } from 'react';
import { clearSession } from "../shared/_helpers/session";
import { useNavigate } from "react-router-dom";
import { Calendar, momentLocalizer } from 'react-big-calendar'
import moment from 'moment';
import 'moment-timezone';
import { logOut } from "../shared/_services/auth.services";
import { useSnackbar } from "notistack";
import Notifications from "./Notifications";
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { sendAppointmentRescheduleRequest, updateAdvisorTimezone, getAllAppointmentsByAdvisor, getSlotsByParticularAdvisor, checkBlockedStatus, getNotifications, markNotificationSeen, saveNotes, getNotes } from "../shared/_services/common.services";
import Loader from "./Loader";
import {
  Button, DatePicker, Dialog, DialogActions, DialogClose, DialogContent,
  DialogTitle, Page, PageHead, Panel, Select,
} from "./ui";
import StarsIcon from "../styles/StarsIcon";

import { Editor } from 'primereact/editor';
import 'primereact/resources/primereact.min.css';

const getInitials = (title) => {
  if (!title) return "";
  const nameParts = title.trim().split(" ");
  if (nameParts.length === 1) {
    return nameParts[0][0].toUpperCase();
  }
  return `${nameParts[0][0]?.toUpperCase() || ""}${nameParts[1][0]?.toUpperCase() || ""}`;
};

// Event colours come from theme classes (eventPropGetter below), not from the data.
const eventClassName = () => 'tw-bg-accent-tint tw-text-text-accent tw-border-0';

const Event = ({ event }) => {
  const profileImage = event.profileImage;
  return (
    <div data-slot="meeting-event" className='tw-flex tw-h-full tw-flex-col tw-justify-between tw-gap-1 tw-p-1'>
      <div className='tw-flex tw-items-center tw-gap-2'>
        {profileImage ? (
          <img
            className='tw-h-6 tw-w-6 tw-flex-none tw-rounded-pill tw-object-cover'
            src={profileImage}
            alt="Profile"
          />
        ) : (
          <span className='tw-m-0 tw-text-xs tw-font-bold'>{getInitials(event.title)}</span>
        )}

        <span className='tw-text-xs tw-font-semibold'>{event?.title?.length > 15 ? `${event?.title?.slice(0, 15)}...` : event?.title}</span>
      </div>
      {event.appointmentStatus === 'completed' &&
        <div className='tw-flex tw-justify-end'>
          <Button size="sm" className='tw-min-h-0 tw-px-2 tw-py-1 tw-text-xs'>
            Details
          </Button>
        </div>}
    </div>
  );
};

const EventDetailBox = ({ event, position, onClose, onOpenEditor, onOpenReschedule }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const handleClickOutside = (mouseEvent) => {
      if (mouseEvent.target.closest('[data-slot="meeting-detail-box"]') === null) {
        setIsVisible(false);
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  if (!isVisible || !event) {
    return null;
  }

  const { start, end, start_url, appointmentStatus } = event;

  return (
    <div
      data-slot="meeting-detail-box"
      className='tw-absolute tw-z-40 tw-w-72 tw-rounded-lg tw-border tw-border-solid tw-border-divider tw-bg-surface tw-p-4 tw-text-text tw-shadow-2'
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <h4 className='tw-m-0 tw-text-md tw-font-bold'>{`Meeting with ${event?.title?.length > 15 ? event?.title?.slice(0, 15) + '...' : event?.title}`}</h4>
      <p className='tw-mb-0 tw-mt-1 tw-text-sm tw-text-text-muted'>{`${moment(start).format('dddd, MMMM D, YYYY')} - ${moment(start).format('h:mm A')} to ${moment(end).format('h:mm A')}`}</p>
      <div className='tw-mt-3 tw-flex tw-flex-wrap tw-gap-2'>
        <Button
          size="sm"
          disabled={appointmentStatus === 'completed'}
          onClick={() => window.open(start_url, "_blank")}
        >
          Join
        </Button>
        <Button
          variant="quiet"
          size="sm"
          disabled={appointmentStatus === 'completed'}
          onClick={() => onOpenReschedule(event)}
        >
          Request to Reschedule
        </Button>
        <Button
          variant="quiet"
          size="sm"
          onClick={() => onOpenEditor(event.appointmentId)}
        >
          <StarsIcon name="edit" className="tw-h-4 tw-w-4 tw-shrink-0" />
          Notes
        </Button>
      </div>
    </div>
  );
};

const CustomToolbar = ({ label, onNavigate }) => {
  const goToBack = () => onNavigate('PREV');
  const goToNext = () => onNavigate('NEXT');
  const goToCurrent = () => onNavigate('TODAY');

  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-p-3">
      <Button variant="quiet" size="sm" onClick={goToCurrent}>Today</Button>
      <div className="tw-flex tw-items-center tw-gap-2">
        <Button variant="icon" size="sm" iconOnly aria-label="Previous week" onClick={goToBack}>
          <StarsIcon name="chevronLeft" className="tw-h-4 tw-w-4 tw-shrink-0" />
        </Button>
        <span className="tw-text-center tw-text-md tw-font-bold tw-text-text">{label}</span>
        <Button variant="icon" size="sm" iconOnly aria-label="Next week" onClick={goToNext}>
          <StarsIcon name="chevron" className="tw-h-4 tw-w-4 tw-shrink-0" />
        </Button>
      </div>
      <span className="tw-w-12 max-rail:tw-hidden" aria-hidden="true" />
    </div>
  );
};

function AdvisorMeeting() {
  const [fromTime, setFromTime] = useState(new Date());
  const [startDate, setStartDate] = useState(new Date());
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [notificationPopupShowAdvisor, setNotificationPopupShowAdvisor] = useState(false);
  const [confirmLogoutPopupShow, setConfirmLogoutPopupShow] = useState(false);
  const [showAdvisorMLoader, setShowAdvisorMLoader] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [boxPosition, setBoxPosition] = useState({ top: 0, left: 0 });
  const [reschedulePopupShow, setReschedulePopupShow] = useState(false);
  const [rescheduleState, setRescheduleState] = useState({
    clientId: '',
    appointmentId: '',
    date: '',
    time: '',
  });
  const [notification, setNotification] = useState([]);
  const [newNotificaiton, setNewNotification] = useState(false);

  const [editorPopupShow, setEditorPopupShow] = useState(false);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [notes, setNotes] = useState({});

  const getAppointments = async () => {
    setShowAdvisorMLoader(true)

    let data = await getAllAppointmentsByAdvisor(JSON.parse(localStorage.getItem('currentUser'))?.user?._id);
    if (data?.status === 200) {
      if (data?.data?.length > 0) {
        setAppointments([...data.data])
      }
    }
    else {
      if (data?.response?.status === 401) {
        clearSession();
        enqueueSnackbar("Your session has been expired!", {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        navigate('/login');
      }
    }
    setShowAdvisorMLoader(false)
  }

  const checkBlocked = async () => {
    const userId = JSON.parse(localStorage.getItem('currentUser')).user?._id;
    try {
      // Call the API to check the user's blocked status
      const response = await checkBlockedStatus(userId);
      if (response.status == 401) {
        clearSession();
        navigate("/login");
      }
      else {
        const { isBlocked, isDeleted } = response.data;

        if (isBlocked || isDeleted) {
          // Clear the currentUser from localStorage
          clearSession();

          // Navigate to the login page
          navigate("/login");
        }
      }
    } catch (error) {
      console.error("Error checking blocked status:", error);
      // Optionally handle errors, e.g., show an error message
    }
  }

  const getNotificationData = async () => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    const userId = currentUser?.user?._id
    const data = await getNotifications(userId);


    if (data.status === 200) {
      const unseenNotifications = data?.data?.data?.filter(notification => !notification.seen);
      setNotification(unseenNotifications);
      if (unseenNotifications.length > 0) {
        setNewNotification(true)
      }
    }
    else {
      if (data?.response?.status === 401) {
        clearSession();
        enqueueSnackbar("No New Notifications", {
          variant: 'success',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        
      }
    }
  }
  const updateAdvisorTimezoneDetails = async () => {
    const currentUserId = JSON.parse(localStorage.getItem("currentUser")).user?._id;
    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; // Browser ka timezone fetch karo
    const body = {
      userId: currentUserId,
      timezone: browserTimeZone
    };

    try {
      // Assuming you have an API function to call
      await updateAdvisorTimezone(body);
    } catch (error) {
      console.error("Error updating timezone:", error);
    }
  }

  useEffect(() => {
    checkBlocked();
    getNotificationData();
    updateAdvisorTimezoneDetails();
    getAppointments();
  }, []);  // eslint-disable-line



  useEffect(() => {
    if (selectedEvent) {
      setStartDate(moment(selectedEvent.start).toDate());
      setFromTime(moment(selectedEvent.start).toDate());
    }
  }, [selectedEvent]);


  const reschedulePopupClose = () => setReschedulePopupShow(false);

  const handleOpenEditor = async (appointmentId) => {
    try {
      const response = await getNotes(appointmentId, "advisor");
      if (response.status === 200) {
        setNotes({ ...notes, [appointmentId]: response.data.data.notes || '' });
        setActiveNoteId(appointmentId);
        setEditorPopupShow(true);
      } else {
        enqueueSnackbar(response.data?.message || "Failed to retrieve notes.", { variant: 'error' });
      }
    } catch (error) {
      console.error("Error retrieving notes:", error);
      enqueueSnackbar("Error retrieving notes.", { variant: 'error' });
    }
  };

  const handleSaveNotes = async (appointmentId) => {
    try {
      const response = await saveNotes(appointmentId, notes[appointmentId], "advisor");
      if (response.status === 200) {
        enqueueSnackbar("Notes saved!", { variant: 'success' });
        setEditorPopupShow(false);
      } else {
        enqueueSnackbar(response.data?.message || "Failed to save notes.", { variant: 'error' });
      }
    } catch (error) {
      console.error("Error saving notes:", error);
      enqueueSnackbar("Error saving notes.", { variant: 'error' });
    }
  };

  const openReschedulePopup = async (event) => {
    const selectedDate = moment(event).format('YYYY-MM-DD');
    const advisorId = JSON.parse(localStorage.getItem('currentUser'))?.user?._id;

    setRescheduleState(prevState => ({
      ...prevState,
      date: selectedDate,
      time: ' ',
      availableTimes: [],
    }));

    const body = {
      date: selectedDate,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      advisorId: advisorId,
    };

    const response = await getSlotsByParticularAdvisor(body);

    if (response?.data?.slots && response?.data?.slots.length > 0) {
      const availableTimes = response.data.slots.map(slot => moment(slot, ["h:mm A"]).format("HH:mm"));
      setRescheduleState(prevState => ({
        ...prevState,
        availableTimes: availableTimes,
        time: availableTimes[0],
      }));
    } else {
      setRescheduleState(prevState => ({
        ...prevState,
        availableTimes: [],
        time: ' ',
      }));

      if (response?.data?.message !== "Slots fetched successfully.") {
        enqueueSnackbar(response?.data?.message, { variant: 'warning' });
      }
    }
    setReschedulePopupShow(true);
  };


  const handleSendRequest = async () => {

    // Get the user's timezone (e.g., America/Los_Angeles)
    const userTimezone = moment.tz.guess();

    // Convert `fromTime` from user's timezone to `client_timezone`
    const convertedTime = moment.tz(fromTime, userTimezone).tz(rescheduleState.client_timezone).format("h:mm A"); // 12-hour format with AM/PM


    const response = await sendAppointmentRescheduleRequest({
      date: moment(startDate).format("YYYY-MM-DD"),
      time: convertedTime, // Use the converted time
      advisorId: JSON.parse(localStorage.getItem("currentUser"))?.user?._id,
      clientId: rescheduleState.clientId,
      appointmentId: rescheduleState.appointmentId,
    });

    if (response.status === 200) {
      enqueueSnackbar("Reschedule request sent successfully!", {
        variant: "success",
        anchorOrigin: { horizontal: "right", vertical: "top" },
      });
      setReschedulePopupShow(false);
      getAppointments();
    } else {
      enqueueSnackbar("Failed to send reschedule request.", {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "top" },
      });
    }
  };


  //logout
  const confirmLogoutPopupClose = () => setConfirmLogoutPopupShow();
  const openConfirmLogoutPopup = () => {
    setConfirmLogoutPopupShow(true);
  };
  const handleLogout = async () => {
    // logOut clears the stored session itself, whatever the server answered,
    // so this always navigates. Gating the clear on a 200 is what made logout
    // unusable: the request had the wrong shape for SEC-115 (#72), so the
    // branch never ran and the only feedback was an error toast. Even with the
    // right shape a 15-minute access token can expire in an open tab, and a
    // logout that refuses to work because the server said 401 is a worse
    // outcome than an orphaned refresh token nobody holds a copy of.
    const data = await logOut();

    if (data?.status !== 200) {
      enqueueSnackbar("Signed out on this device, but the server could not end the session.", {
        variant: "warning",
        anchorOrigin: { horizontal: "right", vertical: "top" },
      });
    }

    navigate("/login");
  };

  const handleNotificationCloseAdvisor = (state) => {
    setNotificationPopupShowAdvisor(state)
  }

  const handleNotificationsClick = async () => {
    setNotificationPopupShowAdvisor(true);
    setNewNotification(false)

    for (const notif of notification) {
      try {
        await markNotificationSeen(notif._id); // Call API for each unseen notification

      } catch (error) {
        console.error(`Error marking notification ${notif._id} as seen:`, error);
      }
    }
  };

  // appointment calender code
  const localizer = momentLocalizer(moment);
  const advisorTimezone = moment.tz.guess(); // Get the current timezone of the advisor

  const events = Array.isArray(appointments)
    ? appointments.map(appointment => {
      const time24 = moment(appointment.time, ["h:mm A"]).format("HH:mm"); // Convert time to 24-hour format
      const date = appointment.date.split('T')[0];

      // Convert appointment time from client's timezone to advisor's timezone
      const startTime = moment.tz(`${date}T${time24}:00`, appointment.timezone);
      const startInAdvisorTz = startTime.clone().tz(advisorTimezone);

      return {
        title: `${appointment?.client?.firstName} ${appointment?.client?.lastName}`,
        appointmentId: `${appointment?._id}`,
        clientId: `${appointment?.clientId}`,
        client_timezone: `${appointment.timezone}`,
        start: startInAdvisorTz.toDate(), // Convert to advisor's timezone
        start_url: appointment?.start_url,
        end: startInAdvisorTz.add(appointment?.duration, 'minutes').toDate(), // Ensure end time is also in advisor's timezone
        profileImage: appointment?.client?.profile_image,
        appointmentStatus: `${appointment?.appointment_status}`,
      };
    })
    : [];

  const handleEventClick = (event, e) => {
    const { clientId, appointmentId, client_timezone } = event;

    setRescheduleState(prevState => ({
      ...prevState,
      clientId,
      client_timezone,
      appointmentId,
      date: moment(event.start).format('YYYY-MM-DD'),
      time: moment(event.start).format('HH:mm'),
    }));
    const rect = e.target.getBoundingClientRect();
    setBoxPosition({
      top: rect.top + window.scrollY + 60,
      left: rect.left + window.scrollX
    });
    setSelectedEvent(event);
  };

  const handleCalendarNavigate = async () => {
    await getAppointments();
  };

  return (
    <Page>
      <PageHead
        title="Meetings"
        tools={
          <>
            <Button variant="quiet" onClick={() => navigate(`/advisor-manage-availability`)}>
              <StarsIcon name="clock" className="tw-h-4 tw-w-4 tw-shrink-0" />
              Manage Availability
            </Button>
            <Button variant="icon" iconOnly className="tw-relative" onClick={handleNotificationsClick} title='Notifications' aria-label='Notifications'>
              <StarsIcon name="bell" className="tw-h-5 tw-w-5 tw-shrink-0" />
              {newNotificaiton && (
                <span data-slot="new-notification" className="tw-absolute tw-right-2 tw-top-2 tw-h-2 tw-w-2 tw-rounded-pill tw-bg-danger" />
              )}
            </Button>
            <Notifications onClose={handleNotificationCloseAdvisor} showModal={notificationPopupShowAdvisor} />
            <Button variant="icon" iconOnly onClick={openConfirmLogoutPopup} title='Log Out' aria-label='Log Out'>
              <StarsIcon name="logout" className="tw-h-5 tw-w-5 tw-shrink-0" />
            </Button>
          </>
        }
      />

      {showAdvisorMLoader ? <Loader /> : <Panel as="div" className="tw-overflow-x-auto tw-p-0 max-rail:tw-p-0">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          defaultView="week"
          views={['week']}
          components={{
            toolbar: CustomToolbar,
            event: Event,
          }}
          eventPropGetter={() => ({ className: eventClassName() })}
          step={60}
          timeslots={1}
          onSelectEvent={handleEventClick}
          onNavigate={handleCalendarNavigate}
        />
        {selectedEvent && (
          <EventDetailBox
            event={selectedEvent}
            position={boxPosition}
            onClose={() => setSelectedEvent(null)}
            onOpenEditor={handleOpenEditor}
            onOpenReschedule={openReschedulePopup}
          />
        )}
      </Panel>}
      {/* reschedule meeting popup */}
      <Dialog open={reschedulePopupShow} onOpenChange={(o) => !o && reschedulePopupClose()}>
        <DialogContent
          aria-describedby={undefined}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
            <DialogTitle>Request to Reschedule</DialogTitle>
            <DialogClose asChild>
              <Button variant="icon" size="sm" iconOnly aria-label="Close">
                <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
              </Button>
            </DialogClose>
          </div>
          <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <div className="tw-min-w-0 tw-flex-1">
              <DatePicker
                dateFormat="MMMM d, yyyy"
                selected={startDate}
                onChange={(date) => { setStartDate(date); openReschedulePopup(moment(date).format('YYYY-MM-DD')); }}
                minDate={new Date()}
              />
            </div>

            {/* --- Time Dropdown instead of DatePicker --- */}
            <div className="tw-min-w-0 tw-flex-1">
              {rescheduleState.availableTimes && rescheduleState.availableTimes.length > 0 ? (
                <Select
                  aria-label="Time"
                  value={rescheduleState.time}
                  onChange={(e) => {
                    const selectedTime = e.target.value; // "HH:mm"
                    setRescheduleState((prev) => ({ ...prev, time: selectedTime }));

                    // update fromTime as Date if needed
                    setFromTime(moment(`${rescheduleState.date} ${selectedTime}`, "YYYY-MM-DD HH:mm").toDate());
                  }}
                >
                  {rescheduleState.availableTimes.map((t) => (
                    <option key={t} value={t}>
                      {moment(t, "HH:mm").format("h:mm A")}
                    </option>
                  ))}
                </Select>
              ) : (
                <p className="tw-m-0 tw-text-sm tw-text-text-muted">No slots available</p>
              )}
            </div>
          </div>
          <DialogActions>
            <Button onClick={handleSendRequest}>Send Request</Button>
          </DialogActions>
        </DialogContent>
      </Dialog>


      {/* confirm logout popup */}
      <Dialog open={!!confirmLogoutPopupShow} onOpenChange={(o) => !o && confirmLogoutPopupClose()}>
        <DialogContent
          aria-describedby={undefined}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
            <DialogTitle className="tw-text-center tw-text-md tw-font-regular tw-text-text-muted">You are attempting to logout of Astra.</DialogTitle>
            <DialogClose asChild>
              <Button variant="icon" size="sm" iconOnly aria-label="Close">
                <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
              </Button>
            </DialogClose>
          </div>
          <p className="tw-m-0 tw-text-center tw-text-md tw-font-bold">Are you sure?</p>
          <DialogActions className="tw-justify-center">
            <Button onClick={handleLogout}>
              <StarsIcon name="check" className="tw-h-4 tw-w-4 tw-shrink-0" />
              Logout
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {/* Editor Popup */}
      <Dialog open={editorPopupShow} onOpenChange={(o) => !o && setEditorPopupShow(false)}>
        <DialogContent
          aria-describedby={undefined}
          className="tw-max-w-3xl"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
            <DialogTitle>Meeting Notes</DialogTitle>
            <DialogClose asChild>
              <Button variant="icon" size="sm" iconOnly aria-label="Close">
                <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
              </Button>
            </DialogClose>
          </div>
          <Editor
            value={notes[activeNoteId] || ''}
            onTextChange={(e) =>
              setNotes({ ...notes, [activeNoteId]: e.htmlValue })
            }
            style={{ height: '300px' }}
          />
          <DialogActions className="tw-justify-center">
            <Button onClick={() => handleSaveNotes(activeNoteId)}>
              <StarsIcon name="save" className="tw-h-4 tw-w-4 tw-shrink-0" />
              Save Notes
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </Page>
  )
}

export default AdvisorMeeting
